import {
  ChangeDetectionStrategy, Component, DestroyRef, ElementRef,
  computed, effect, inject, signal, untracked, viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { FocusTrapDirective } from '../../../../shared/directives/focus-trap.directive';
import { AnalyticsService } from '../../../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../../../core/analytics/events';
import { FeatureFlagsViewerService } from '../../../../core/services/feature-flags-viewer.service';
import { ShellStateService } from '../../../../core/services/shell-state.service';
import { LocaleService } from '../../../../core/services/locale.service';
import { DetailPanelService } from '../../../../core/services/detail-panel.service';
import { PersonPanelService } from '../../../../shared/people/person-panel.service';
import { PERSON_CALLS } from '../../../../shared/people/person-calls';
import { VoiceInputService } from '../../../../core/services/voice-input.service';
import type { VoiceErrorKind } from '../../../../core/utils/voice-support';
import { AppLaunchService } from '../../../../core/services/app-launch.service';
import { AppActionRegistryService } from '../../../../core/actions/app-action-registry.service';
import { openExternal } from '../../../../core/tauri-runtime';
import {
  AI_CONVERSATION_PERSISTED,
  AIConversationService,
} from '../../../../core/services/ai-conversation.service';
import { CmdkAskService, isPhoneWidth } from './services/cmdk-ask.service';
import { CmdkAppProvider } from './services/cmdk-providers/app.provider';
import { parseSpokenHandOff } from '../../../../core/utils/voice-support';
import { shouldAutoAsk } from './models/cmdk-auto-ask';
import type { CmdkAutoAskSnapshot } from './models/cmdk-auto-ask';
import { resolveEscape } from './models/cmdk-escape';
import type { CmdkEscapeSnapshot } from './models/cmdk-escape';
import type { CmdkIntent, CmdkKeyContext } from './models/cmdk-keymap';
import { ghostCompletion } from './models/cmdk-ranking';
import { quickOpenRows } from './models/cmdk.model';
import type { CmdkItem, CmdkPersonItem } from './models/cmdk.model';
import { CmdkKeymapService } from './services/cmdk-keymap.service';
import { CmdkRecentsService } from './services/cmdk-recents.service';
import { AiTriggerRouterService } from '../../ai-trigger-router.service';
import { CmdkSearchService } from './services/cmdk-search.service';
import { MapFindService } from '../../../../core/services/map-find.service';
import { initialsOf } from '../../../../shared/people/people-kit.model';
import { CmdkStateService } from './services/cmdk-state.service';
import { CmdkInputRowComponent } from './chrome/cmdk-input-row.component';
import { UserPreferencesService } from '../../../../core/services/user-preferences.service';
import { CmdkCoachMarkComponent } from './chrome/cmdk-coach-mark.component';
import { CmdkScopeChipsComponent } from './chrome/cmdk-scope-chips.component';
import { CmdkFooterComponent } from './chrome/cmdk-footer.component';
import { CmdkResultsPaneComponent } from './results/cmdk-results-pane.component';
import { CmdkPreviewPaneComponent } from './preview/cmdk-preview-pane.component';

/**
 * Voice error kind → i18n key.
 *
 * An explicit map rather than string interpolation: the kinds are hyphenated
 * and the keys are not, so `error_${kind}` silently produced a key that does
 * not exist — and transloco renders a missing key as the key itself.
 */
/**
 * Kinds that came off the network. Used only to stamp
 * `CMDK_RESULT_SELECTED.from_remote`, which is how we answer whether the
 * remote search earned its embedding cost. Keep in step with
 * `CmdkSearchService.remoteProviders`.
 */
const REMOTE_KINDS: ReadonlySet<string> = new Set(['space', 'person', 'news']);

/**
 * How long the query must stand still before a zero-result ask fires.
 *
 * Deliberately much longer than the remote search's 220ms. A search is cheap
 * and reversible; an ask is a model call that appends a turn to a visible
 * conversation. Firing per keystroke turned "can you book me a desk for
 * tomorrow" into five separate requests — one for "tomor", "tomorr",
 * "tomorro" and the whole thing — each with its own bubble in the thread.
 */
const AUTO_ASK_DELAY_MS = 700;

/** `no-speech` has no words on purpose: it is silence, not a failure (see the effect below). */
const VOICE_ERROR_KEYS: Readonly<Record<Exclude<VoiceErrorKind, 'no-speech'>, string>> = {
  denied: 'shell.cmdk.voice.error_denied',
  'no-mic': 'shell.cmdk.voice.error_no_mic',
  unsupported: 'shell.cmdk.voice.error_unsupported',
  generic: 'shell.cmdk.voice.error_generic',
};

/**
 * Command K — the two-pane command palette.
 *
 * Mounted once at shell level, and the only command palette in the app: the
 * legacy `im-command-palette` was deleted at cutover, so ⌘K, ⌘P, the search
 * pill and the desktop menu item all reach this component.
 *
 * This component is the ORCHESTRATOR. It owns no data and computes no results:
 * it wires the keyboard layer to the state, dispatches intents, and hands the
 * panes what they need. Everything interesting is in a pure model or a service
 * that can be tested without it.
 *
 * See SPEC-command-k.md for the full design, the rejected alternatives, and
 * the reasons behind the Esc ladder and the Ask row's placement.
 */
@Component({
  selector: 'im-cmdk',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FocusTrapDirective,
    CmdkInputRowComponent,
    CmdkScopeChipsComponent,
    CmdkCoachMarkComponent,
    CmdkFooterComponent,
    CmdkResultsPaneComponent,
    CmdkPreviewPaneComponent,
  ],
  templateUrl: './cmdk.component.html',
  styleUrl: './cmdk.component.scss',
  host: { class: 'ph-no-capture' },
  // The palette runs its OWN conversation. `AIConversationService` holds one
  // message list, one conversationId, one abort handle and an `isStreaming`
  // flag that acts as a mutual-exclusion lock — so sharing the root instance
  // means the palette either destroys the docked panel's thread (what the
  // legacy palette does) or is silently refused while the panel streams.
  //
  // Providing it here gives the palette a genuinely separate conversation.
  // Nothing is needed from gemba-ai: ids are server-issued per POST with no
  // per-user constraint and history is client-supplied, so two live
  // conversations already work. See CmdkAskService for the full reasoning.
  //
  // NOT PERSISTED. The root instance mirrors its thread to sessionStorage so
  // the docked panel survives a reload; this one must not, or the palette's
  // scratch ask would restore the panel's transcript on open and overwrite
  // it on every keystroke — the two instances would share one key.
  providers: [
    AIConversationService,
    { provide: AI_CONVERSATION_PERSISTED, useValue: false },
    CmdkAskService,
  ],
})
export class CmdkComponent {
  protected readonly state = inject(CmdkStateService);
  protected readonly search = inject(CmdkSearchService);

  private readonly keymap = inject(CmdkKeymapService);
  protected readonly voice = inject(VoiceInputService);
  private readonly appLaunch = inject(AppLaunchService);
  private readonly appActions = inject(AppActionRegistryService);
  private readonly appProvider = inject(CmdkAppProvider);
  protected readonly ask = inject(CmdkAskService);
  /** The DOCKED panel's conversation — the hand-off target, from root. */
  private readonly panelConversation = inject(AIConversationService, { skipSelf: true });
  private readonly featureFlags = inject(FeatureFlagsViewerService);
  private readonly shell = inject(ShellStateService);
  private readonly analytics = inject(AnalyticsService);
  private readonly announcer = inject(LiveAnnouncer);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userPrefs = inject(UserPreferencesService);
  private readonly detailPanel = inject(DetailPanelService);
  /** The map's one reveal; connected only while the map page is open. */
  private readonly mapFind = inject(MapFindService);
  private readonly personPanel = inject(PersonPanelService);
  /** Flow Live: Call on a colleague's preview (off unless `flow.calls`). */
  private readonly personCalls = inject(PERSON_CALLS);
  private readonly recents = inject(CmdkRecentsService);
  private readonly aiTrigger = inject(AiTriggerRouterService);

  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');
  private readonly inputRow = viewChild(CmdkInputRowComponent);
  private readonly resultsPane = viewChild(CmdkResultsPaneComponent);

  /** True while ⌘ or Ctrl is held — reveals the quick-open badges. */
  protected readonly modifierHeld = signal(false);

  protected readonly listboxId = 'cmdk-results-listbox';

  protected readonly aiEnabled = this.featureFlags.isEnabled('ai');

  /** Rendered only while the palette is open. */
  protected readonly visible = computed(() => this.state.open());

  /** Hides the first-run tip band once the user has dismissed it. */
  protected readonly coachMarkSeen = this.userPrefs.cmdkCoachMarkSeen;

  protected readonly dialogLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.dialog_label');
  });

  protected readonly listLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('shell.cmdk.results_label');
  });

  private detachKeymap: (() => void) | null = null;
  private voiceStartedAt = 0;
  /** For CMDK_DISMISSED's dwell. `performance.now()` at the last open. */
  private openedAt = 0;
  /** Previous value of `scope()`, for the change event's `from`. */
  private lastScope: string = 'all';
  /**
   * Set by `activate()` so the dismissal that follows reports success rather
   * than abandonment. Cleared on every open.
   */
  private closingBecauseActivated = false;
  /** The debounced zero-result ask, if one is armed. */
  private pendingAutoAsk: ReturnType<typeof setTimeout> | null = null;
  /**
   * An auto-ask has already failed this session. Suppresses further ones —
   * see the effect. Cleared when the palette reopens, and by an EXPLICIT ask,
   * which is the user saying "try anyway".
   */
  private autoAskFailed = false;

  constructor() {
    // Commit each completed utterance exactly once. `consumeFinal()` clears
    // the signal as it reads, so a re-render cannot replay the same transcript.
    // Only while the palette is open: this component is always mounted, and
    // the Ask composer dictates through the same service.
    effect(() => {
      if (!this.voice.finalText() || !this.state.open()) return;
      this.commitTranscript(this.voice.consumeFinal());
    });

    // Failures are announced, not rendered — the palette has one live region
    // and dictation is transient. `no-speech` is deliberately silent: the user
    // simply did not say anything, and telling them so is nagging.
    effect(() => {
      const kind = this.voice.error();
      if (!kind || kind === 'no-speech') return;
      this.analytics.capture(ANALYTICS_EVENTS.CMDK_VOICE_ERROR, { kind });
      // The kinds are hyphenated (`no-mic`) and the i18n keys are not, so
      // interpolating the kind straight into the key produced
      // `voice.error_no-mic`, which does not exist — and transloco renders a
      // missing key as the key itself. The user heard their screen reader say
      // "shell.cmdk.voice.error_no-mic".
      this.announce(this.transloco.translate(VOICE_ERROR_KEYS[kind]));
      this.voice.clearError();
    });

    // Bind and release the keyboard layer with the palette's visibility. The
    // lease on HotkeysService MUST be released on close, or ⌘K, ⌘1–6 and ⌘B
    // stay dead for the rest of the session.
    effect(() => {
      const open = this.visible();
      const element = this.panel()?.nativeElement;
      if (open && element && !this.detachKeymap) {
        this.detachKeymap = this.keymap.attach(element, {
          context: () => this.keyContext(),
          dispatch: intent => this.dispatch(intent),
          setModifierHeld: held => this.modifierHeld.set(held),
        });
      } else if (!open && this.detachKeymap) {
        this.detachKeymap();
        this.detachKeymap = null;
        this.modifierHeld.set(false);
      }
    });

    // Belt and braces: a destroy that races the effect must not leak the lease.
    this.destroyRef.onDestroy(() => {
      this.detachKeymap?.();
      this.detachKeymap = null;
      this.cancelPendingAutoAsk();
    });

    // Zero results auto-runs the answer.
    //
    // Asking permission at the moment confidence is lowest is the worst
    // possible time to ask: nothing else is on screen, so "press ⌘↵ to ask"
    // is a dead end wearing a hint. Guarded on the ask row being the ONLY
    // selectable row, which `buildRows` guarantees for this case.
    effect(() => {
      if (!this.visible() || !this.ask.enabled()) return;
      const rows = this.search.selectable();
      const onlyAsk = rows.length === 1 && rows[0].item.kind === 'ask';
      const query = this.state.effectiveQuery();

      // Everything below is read untracked: this effect WRITES the ask
      // service's state, and `ask.reset()` (the Esc ladder's dismiss rung)
      // writes `_query`, so a tracked read would let every dismissal dirty
      // this effect and bill another model call.
      untracked(() => {
        this.cancelPendingAutoAsk();
        // Not on a phone: the pane that would show the answer is not drawn there.
        if (isPhoneWidth()) return;
        if (!this.autoAskSnapshot(onlyAsk, query).query) return;
        if (!shouldAutoAsk(this.autoAskSnapshot(onlyAsk, query))) return;

        // The DEBOUNCE. `shouldAutoAsk` answers "is this worth asking"; only a
        // timer can answer "has the user finished typing", and without it every
        // keystroke was its own model call and its own turn in the thread.
        this.pendingAutoAsk = setTimeout(() => {
          this.pendingAutoAsk = null;
          // Re-decide on the way out. 700ms is long enough for the user to
          // have typed something that now matches, or closed the palette.
          const fresh = this.autoAskSnapshot(this.search.resultCount() === 1, this.state.effectiveQuery());
          if (!shouldAutoAsk(fresh)) return;
          this.ask.ask(fresh.query, 'zero-results', 0);
        }, AUTO_ASK_DELAY_MS);
      });
    });

    // Arm the circuit breaker. Watching the ask's own error rather than the
    // request's return value, because the failure surfaces asynchronously
    // through the conversation's state.
    effect(() => {
      const failed = this.ask.error() !== null;
      untracked(() => {
        if (failed) this.autoAskFailed = true;
      });
    });

    // One analytics event per open, with the source the opener stamped rather
    // than one inferred by sniffing document.activeElement.
    effect(() => {
      if (!this.visible()) return;
      // `scope` is read UNTRACKED. It is a payload field, not a trigger: a
      // tracked read makes this fire again every time the user tabs a chip,
      // so one open reports as five.
      untracked(() => {
        this.analytics.capture(ANALYTICS_EVENTS.CMDK_OPENED, {
          source: this.state.openSource(),
          scope: this.state.scope(),
        });
        this.openedAt = performance.now();
        // A fresh session gets a fresh attempt: the assistant may have come
        // back, and the previous failure was about the previous session.
        this.autoAskFailed = false;
      });
    });

    // Scope changes, from BOTH routes.
    //
    // `setScope()` is not the choke point it looks like: `scope` is a computed
    // where a typed prefix (`p `, `:`, `>`) overrides the chip without going
    // near the setter, so instrumenting the method would miss half the
    // changes. Watching the computed catches both and cannot be bypassed.
    //
    // `prefix()` is read untracked — it is how we label the change, not what
    // triggers it, and a tracked read would fire this again on every keystroke
    // behind an active pill.
    effect(() => {
      const to = this.state.scope();
      untracked(() => {
        if (!this.visible()) {
          // Remember where a fresh session starts, so the first real change
          // reports the right `from` rather than a stale one.
          this.lastScope = to;
          return;
        }
        const from = this.lastScope;
        if (from === to) return;
        this.lastScope = to;
        this.analytics.capture(ANALYTICS_EVENTS.CMDK_SCOPE_CHANGED, {
          from,
          to,
          via: this.state.prefix() ? 'prefix' : 'chip',
        });
      });
    });
  }

  // ── Keyboard ──────────────────────────────────────────────────────

  private keyContext(): CmdkKeyContext {
    const input = this.inputRow();
    return {
      actionMenuOpen: this.state.actionMenuOpen(),
      // Flows arrive in slice 8; until then the palette is never in one.
      inFlow: false,
      listening: this.voice.listening(),
      queryEmpty: this.state.query().length === 0,
      hasPrefix: this.state.prefix() !== null,
      caretAtEnd: input?.caretAtEnd() ?? false,
      hasTextSelection: input?.hasTextSelection() ?? false,
      hasGhost: ghostCompletion(this.search.topItem(), this.state.query()) !== null,
      hasSelection: this.search.resultCount() > 0,
      aiEnabled: this.aiEnabled(),
      hasAnswer: this.ask.hasAnswer(),
      voiceSupported: this.voice.supported(),
    };
  }

  private dispatch(intent: CmdkIntent): void {
    const count = this.search.resultCount();

    switch (intent.type) {
      case 'escape':
        this.applyEscape();
        return;

      case 'close-palette':
        this.close();
        return;

      case 'move':
        this.state.moveSelection(intent.delta, count);
        return;

      case 'move-edge':
        this.state.moveSelectionToEdge(intent.edge, count);
        return;

      case 'activate': {
        const item = this.search.selectedItem();
        if (item) this.activate(item, 'enter');
        return;
      }

      case 'quick-open': {
        const row = quickOpenRows(this.search.rows())[intent.index];
        if (row) {
          this.state.setSelectedIndex(row.flatIndex);
          this.activate(row.item, 'quick-open');
        }
        return;
      }

      case 'cycle-scope':
        this.state.cycleScope(intent.delta);
        this.resultsPane()?.scrollToTop();
        return;

      case 'accept-ghost': {
        const remainder = ghostCompletion(this.search.topItem(), this.state.query());
        if (remainder) this.state.setQuery(this.state.query() + remainder);
        return;
      }

      case 'open-action-menu':
        // Deliberately inert until the menu component lands (slice 10).
        //
        // Setting `actionMenuOpen` here would soft-lock the keyboard: with no
        // popover rendered, `mapKey`'s first branch takes over and swallows
        // every arrow, Tab and Enter, and the only key that still does
        // anything is Escape. An affordance that has not shipped must not be
        // reachable, not merely invisible.
        return;

      case 'close-action-menu':
        this.state.setActionMenuOpen(false);
        return;

      case 'help':
        // The cheatsheet becomes an in-palette flow in slice 8. Until then it
        // opens the shell's own shortcuts dialog — the same content, and the
        // dialog stamps its own analytics source. Closing the palette WITHOUT
        // opening anything, which is what this used to do, reads as `?` having
        // dismissed the palette for no reason.
        this.shell.openShortcutsDialog('command-palette');
        this.close();
        return;

      case 'toggle-voice':
        this.toggleVoice('shortcut');
        return;

      case 'ask':
        this.startAsk('shortcut');
        return;

      case 'continue-in-chat':
        this.continueInChat();
        return;

      case 'route-ai':
        this.routeAi();
        return;

      case 'toggle-answer-room':
        this.state.toggleAnswerRoom();
        return;

      // Arriving in later slices. Listed rather than defaulted so adding an
      // intent is a compile error here until it is handled.
      case 'instant-book':
      case 'copy-link':
      case 'clear-prefix':
      case 'action-menu-move':
      case 'action-menu-activate':
        this.dispatchPending(intent);
        return;
    }
  }

  /** Intents whose home slice has not landed. Kept explicit, never silent. */
  private dispatchPending(intent: CmdkIntent): void {
    if (intent.type === 'clear-prefix') {
      this.state.clearPrefix();
      return;
    }
    if (intent.type === 'action-menu-move' || intent.type === 'action-menu-activate') {
      // The menu itself lands in slice 10; it can never be open before then.
      return;
    }
    // ask / instant-book / copy-link / voice / answer-room: slices 7, 8, 9, 10.
  }

  /**
   * HotkeysService is suspended while the palette owns the keyboard, so the
   * palette must claim ⌘J itself. AiTriggerRouterService preserves the same
   * route-aware destination as the shell binding.
   */
  private routeAi(): void {
    this.closingBecauseActivated = true;
    this.close();
    this.aiTrigger.route('command-palette');
  }

  protected applyEscape(): void {
    const snapshot: CmdkEscapeSnapshot = {
      listening: this.voice.listening(),
      actionMenuOpen: this.state.actionMenuOpen(),
      flowDepth: 0,
      paneMode: this.state.paneMode(),
      hasAnswer: this.ask.hasAnswer(),
      hasScope: this.state.hasScope(),
      hasQuery: this.state.hasQuery(),
    };

    const step = resolveEscape(snapshot);
    switch (step.type) {
      case 'close-action-menu': this.state.setActionMenuOpen(false); return;
      case 'shrink-answer': this.state.setPaneMode(step.to); return;
      case 'clear-query': this.state.clearQuery(); return;
      case 'clear-scope': this.state.clearScope(); return;
      case 'close': this.close(); return;
      // Stopping keeps whatever was heard — Esc while dictating means "I am
      // done talking", not "throw that away".
      case 'stop-voice': this.voice.stop(); return;
      // Flows and the answer arrive in slices 7-8; their rungs cannot be
      // reached until the snapshot above can report them.
      case 'dismiss-answer': this.ask.reset(); return;
      // Flows arrive in slice 8.
      case 'pop-flow': return;
    }
  }

  // ── Activation ────────────────────────────────────────────────────

  /**
   * Call on a colleague's preview (Flow Live, 0sm8.4): the palette closes as for any activation and the call dock
   * takes over. The person is remembered like an opened row.
   */
  protected callPerson(item: CmdkPersonItem): void {
    if (!this.personCalls.canCall(item.id)) return;
    this.closingBecauseActivated = true;
    this.recents.record(item);
    this.close();
    this.personCalls.call(item.id, item.title, 'cmdk');
  }

  protected activate(item: CmdkItem, via: 'enter' | 'click' | 'quick-open'): void {
    // Every close() below this line is a SUCCESS, not an abandonment. The flag
    // is read and cleared by `captureDismissal`, so the ten call sites do not
    // each have to remember to say so.
    this.closingBecauseActivated = true;

    // Remember it, before anything navigates. The service decides which kinds
    // are worth remembering — the palette does not need to know the rule.
    this.recents.record(item);

    this.analytics.capture(ANALYTICS_EVENTS.CMDK_RESULT_SELECTED, {
      kind: item.kind,
      group: this.state.scope(),
      position: this.state.selectedIndex(),
      query_length: this.state.effectiveQuery().length,
      via,
      from_remote: REMOTE_KINDS.has(item.kind),
    });

    if (item.kind === 'nav' || item.kind === 'action') {
      // The registry's own handler. Contributors keep working unchanged.
      void item.command.execute();
      this.close();
      return;
    }

    if (item.kind === 'ask') {
      if (isPhoneWidth()) {
        if (this.ask.askOnPage(item.query, 'row', this.search.resultCount())) this.close();
        return;
      }
      this.ask.ask(item.query, 'row', this.search.resultCount());
      return;
    }

    if (item.kind === 'app' && item.actionId) {
      // A part of RICOH Workplace: the same entry the app menu runs, with
      // the same gate and the same handler (Settings goes to /admin or
      // /profile depending on who is asking).
      this.appActions.run(item.actionId, 'command-palette');
      this.close();
      return;
    }

    if (item.kind === 'app' && item.external) {
      // Another Workplace app on its own origin (Manage). The provider built
      // the URL from configuration and refuses anything but http(s).
      void openExternal(item.url);
      this.close();
      return;
    }

    if (item.kind === 'app') {
      // AppLaunchService owns HTTPS validation, the in-app viewer route for
      // PDFs and images, its own analytics and the click tracking. The palette
      // must not re-implement any of it — a second `window.open` path is how
      // one surface quietly stops validating URLs.
      const link = this.appProvider.linkFor(item.id);
      if (link) this.appLaunch.launch(link, 'cmdk');
      this.close();
      return;
    }

    if (item.kind === 'space') {
      // `DetailPanelService` owns the whole space record — availability,
      // amenities, the booking form and the map focus. The palette hands the
      // id over and gets out of the way rather than re-deriving any of it;
      // the id is all `openForSpace` needs, which is what makes this work on
      // the estate-wide search path where there is no locationId to focus.
      this.detailPanel.openForSpace(item.id, item.floorName, 'palette');
      // On the map, the palette's room is shown the way the map's own search shows it: the camera goes there and it
      // is outlined and tagged (MapFindService). Off the map nothing is connected and this does nothing.
      // Only a room in the building on screen: a row from another building
      // (the estate fallback) carries no locationId and has nothing to fly to.
      if (this.mapFind.connected() && item.locationId) {
        const noun = (item.spaceType ?? '').toLowerCase();
        const spaceKind = noun.includes('desk') ? 'desk' : noun.includes('parking') ? 'parking' : 'room';
        void this.mapFind.reveal({ kind: 'space', spaceKind, spaceId: item.id, floorId: item.floorId ?? null, label: item.title }, 'palette');
      }
      this.close();
      return;
    }

    if (item.kind === 'person') {
      // On the map the palette is the map's search (the map workspace): the colleague is also found on the map — their
      // seat from the map's own roster, ringed and tagged (MapFindService). Off the map nothing is connected.
      if (this.mapFind.connected()) {
        void this.mapFind.reveal({ kind: 'person', personId: item.id, name: item.title, initials: initialsOf(item.title), photoUrl: item.pictureUrl ?? null }, 'palette');
      }
      // The person panel (PersonPanelService, z12i.54): a colleague opens in
      // the side panel of wherever you are, as from Ask, a face or a chip,
      // and their full page (/people/:id) is one link away in it.
      this.personPanel.open(item.id, {
        from: 'search',
        seed: {
          id: item.id,
          name: item.title,
          ...(item.email ? { email: item.email } : {}),
          ...(item.jobTitle ? { jobTitle: item.jobTitle } : {}),
          ...(item.department ? { department: item.department } : {}),
          picture: item.pictureUrl ?? null,
        },
      });
      this.close();
      return;
    }

    if (item.kind === 'news') {
      // Two destinations behind one group. A knowledge page needs BOTH ids —
      // `/flow/knowledge/:workspaceId/:pageId` — so a page whose workspace did
      // not come back cannot be opened, and silently routing to the article
      // page instead would land the user on a 404 with no explanation.
      if (item.newsType === 'video' && /^(article|event):/i.test(item.id)) {
        void this.router.navigate(['/videos'], { queryParams: { v: item.id } });
      } else if (item.newsType === 'page') {
        if (!item.workspaceId) return;
        void this.router.navigate(['/flow', 'knowledge', item.workspaceId, item.id]);
      } else {
        // `/article/:id` — THE READER, and the one door the palette shares
        // with Discover, the run's "Read it" and the reader's own tail.
        //
        // This was `['/home', 'article', item.id]` until this change, and that
        // is a DIFFERENT SURFACE: `/home/article/:id` loads the
        // previous-generation home and lets `ArticleDetailService.open()`
        // portal `im-article-detail-panel` over it — the overlay with no URL
        // of its own, which `openReader` in `discover-page.component.ts` left
        // behind for exactly the reasons named there (no shareable link, a
        // reload loses the article, Back closes the page under it).
        //
        // ⚠ WRITTEN AS A LITERAL, deliberately. `ARTICLE_READER_ROUTE_PREFIX`
        // exists in `core/constants/navigation.ts` and using it here would
        // make this door invisible to `articleDoorsInSource`, which matches
        // the written-out route array and nothing else. A door the counter
        // cannot see is the whole failure `discover-article-doors.spec.ts`
        // and `content-doors.spec.ts` were written against.
        void this.router.navigate(['/article', item.id]);
      }
      this.close();
      return;
    }

  }

  // ── Ask ───────────────────────────────────────────────────────────

  /** ⌘↵, or the ask row. */
  protected startAsk(trigger: 'row' | 'shortcut' | 'zero-results' | 'voice'): void {
    const query = this.state.effectiveQuery();
    if (!query) return;
    // Asking on purpose is the user saying "try anyway", so it re-arms the
    // automatic path too — otherwise one early failure would silence the
    // zero-result ask for the rest of the session even after the assistant
    // recovered.
    this.autoAskFailed = false;
    this.cancelPendingAutoAsk();
    // A phone has no preview column for the answer (ruling 17): it goes on at /ai.
    if (isPhoneWidth()) {
      if (this.ask.askOnPage(query, trigger, this.search.resultCount())) this.close();
      return;
    }
    this.ask.ask(query, trigger, this.search.resultCount());
    this.selectAskRow();
  }

  /**
   * Hand the conversation to the docked panel.
   *
   * The panel ADOPTS this thread rather than starting a new one, so the user
   * lands mid-exchange instead of at a blank composer. That is the whole
   * reason the palette runs a separate conversation.
   */
  protected continueInChat(): void {
    // `handOffToPanel` guards both conditions itself — this mirrors them only
    // so the palette does not close on a hand-off that will not happen.
    if (!this.ask.hasAnswer() || this.ask.streaming()) return;
    this.ask.handOffToPanel(this.panelConversation);
    this.close();
  }

  /**
   * An Ask widget's "View on map". The same hand-over a space result makes:
   * `DetailPanelService` owns the record and the map focus; the palette
   * gets out of the way.
   */
  protected viewSpaceFromAsk(spaceId: string): void {
    if (!spaceId) return;
    this.detailPanel.openForSpace(spaceId, undefined, 'palette');
    this.close();
  }

  /** Put the selection on the ask row, so the pane shows the answer. */
  private selectAskRow(): void {
    const row = this.search.selectable().find(r => r.item.kind === 'ask');
    if (row) this.state.setSelectedIndex(row.flatIndex);
  }

  // ── Voice ─────────────────────────────────────────────────────────

  /**
   * Start or stop dictation. MUST run from a user gesture — a browser will not
   * grant microphone access to a programmatic call, and Chromium fails that
   * silently rather than rejecting. Both call sites (the mic button's click
   * and the ⌥Space keydown) are gestures.
   */
  protected toggleVoice(trigger: 'mic' | 'shortcut'): void {
    if (!this.voice.supported()) return;

    if (this.voice.listening()) {
      this.voice.stop();
      return;
    }

    this.voiceStartedAt = performance.now();
    this.voice.start();
    this.analytics.capture(ANALYTICS_EVENTS.CMDK_VOICE_STARTED, {
      trigger,
      locale: this.locale.locale(),
    });
    // Announced rather than rendered as a second live region — the footer's
    // result count is the palette's only one, and two polite regions updating
    // in the same tick read over each other.
    this.announce(this.transloco.translate('shell.cmdk.voice.listening'));
  }

  /**
   * Commit a completed utterance.
   *
   * Only FINAL transcripts reach here. Interim results are shown ghosted in
   * the field and never touch the query, so the results list does not strobe
   * once per revision while someone is still talking.
   */
  private commitTranscript(raw: string): void {
    const { text, handOff } = parseSpokenHandOff(raw);
    if (!text) return;

    this.state.setQuery(text);
    this.analytics.capture(ANALYTICS_EVENTS.CMDK_VOICE_COMMITTED, {
      transcript_length: text.length,
      duration_ms: Math.round(performance.now() - this.voiceStartedAt),
      handed_to_ai: handOff && this.aiEnabled(),
    });

    // "… ask AI" routes the rest straight to the assistant. Saying it is the
    // only way to reach Ask without touching the keyboard, which is the whole
    // point of dictating.
    if (handOff && this.aiEnabled()) {
      if (isPhoneWidth()) {
        if (this.ask.askOnPage(text, 'voice', this.search.resultCount())) this.close();
        return;
      }
      this.ask.ask(text, 'voice', this.search.resultCount());
      this.selectAskRow();
    }
    this.inputRow()?.focus();
  }

  /** Focus back in the field — after a "found in People" hint switched the chip. */
  protected focusInput(): void {
    this.inputRow()?.focus();
  }

  // ── Open / close ──────────────────────────────────────────────────

  protected close(reason: 'escape' | 'backdrop' | 'route-change' = 'escape'): void {
    // Read the session's shape BEFORE tearing it down — `ask.end()` clears
    // the answer and `state.close()` clears the query, so measuring after
    // would report every session as empty.
    this.captureDismissal(this.closingBecauseActivated ? 'activated' : reason);

    // …nor an ask armed to fire into a palette that is no longer on screen.
    this.cancelPendingAutoAsk();
    // A palette that closes mid-utterance must not leave the mic hot.
    this.voice.abort();
    // …nor a request in flight, nor an answer waiting to be re-shown. Without
    // this the next ⌘K inherits the previous session's answer, and the first
    // Escape spends itself dismissing something the user cannot see.
    this.ask.end();
    this.state.close();
    // Hand focus back to whatever opened the palette. When there is nothing to
    // return to — a hotkey pressed with focus on the body — the focus trap's
    // own restore takes over rather than stranding focus on the panel.
    this.state.restoreFocus();
  }

  protected onBackdropPointerDown(event: MouseEvent): void {
    // Only a click on the scrim itself. Without the target check, releasing a
    // text selection that started inside the panel closes it.
    if (event.target === event.currentTarget) this.close('backdrop');
  }

  /**
   * The other half of CMDK_OPENED.
   *
   * `activated` is the success case, so opened → dismissed is the whole funnel
   * and abandonment is `dismissed where reason != 'activated'`. Without this
   * event a palette nobody ever picks anything from is indistinguishable from
   * one that works, because only the successes were being reported.
   */
  private captureDismissal(reason: 'escape' | 'backdrop' | 'activated' | 'route-change'): void {
    this.closingBecauseActivated = false;
    // Guard the double-close: `toggle()` and the Esc ladder can both land here
    // for one session, and a second event would double-count the abandonment.
    if (!this.state.open()) return;

    this.analytics.capture(ANALYTICS_EVENTS.CMDK_DISMISSED, {
      reason,
      query_length: this.state.effectiveQuery().length,
      result_count: this.search.resultCount(),
      had_answer: this.ask.hasAnswer(),
      dwell_ms: Math.round(performance.now() - this.openedAt),
    });
  }

  /** The decision's inputs, in one place. See `models/cmdk-auto-ask.ts`. */
  private autoAskSnapshot(askIsOnlyRow: boolean, query: string): CmdkAutoAskSnapshot {
    return {
      visible: this.visible(),
      aiEnabled: this.ask.enabled(),
      askIsOnlyRow,
      query,
      lastAsked: this.ask.query(),
      failedThisSession: this.autoAskFailed,
      searching: this.search.anyRemoteLoading(),
    };
  }

  /** Drop an armed zero-result ask. Called whenever the query moves. */
  private cancelPendingAutoAsk(): void {
    if (this.pendingAutoAsk === null) return;
    clearTimeout(this.pendingAutoAsk);
    this.pendingAutoAsk = null;
  }

  /** Announce a transient message without a second live region. */
  protected announce(message: string): void {
    void this.announcer.announce(message, 'polite');
  }

  /** Navigate and close — the shared exit for every result that is a place. */
  protected navigateTo(route: string): void {
    void this.router.navigate([route]);
    this.close();
  }
}
